import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FilePenLine, Download, Filter } from "lucide-react";
import { convertDateToCT, countActiveFilters } from "../../utils/util";
import { downloadPayoutCase, listAllPayoutsCase } from "../../reducers/payouts.slice";
import { AuthContext } from "../../context/authContext";
import { Button } from "../../components/ui/button";
import DataTable from "../../components/shared/DataTable";
import PageHeader from "../../components/shared/PageHeader";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import StatusBadge from "../../components/shared/StatusBadge";
import useMediaQuery from "../../components/shared/useMediaQuery";
import PayoutCasesPages from "./PayoutCasesPage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const CASE_STATUSES = ["NEW", "UNDER_INVESTIGATION", "RESOLVED"];

const statStyle = (accent) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: 14,
  padding: "16px 18px",
});

const statLabelStyle = {
  display: "block",
  color: "#c3c8cc",
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.09em",
};

const statValueStyle = {
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: 24,
  fontWeight: 800,
  marginTop: 8,
};

const PaymentCasesPages = () => {
  const [showLoader, setShowLoader] = useState(false);
  const [payoutCases, setPayoutCases] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const stored = sessionStorage.getItem("paymentResolution_selectedStatuses");
    return stored ? JSON.parse(stored) : [];
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [caseRow, setCaseRow] = useState(null);

  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const getAllPayoutCases = () => {
    setShowLoader(true);
    dispatch(listAllPayoutsCase()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formatted = res?.payload?.map((item) => ({
        ...item,
        createdDate: convertDateToCT(item?.created_at, "MMMM D, YYYY hh:mm:ss A"),
        expectedDeliveryDate: convertDateToCT(item?.expected_delivery_date, "MMMM D, YYYY hh:mm:ss A"),
        actualDeliveryDate: convertDateToCT(item?.actual_delivery_date, "MMMM D, YYYY hh:mm:ss A"),
      }));
      setShowLoader(false);
      setPayoutCases(formatted);
    });
  };

  useEffect(() => { getAllPayoutCases(); }, []);

  useEffect(() => {
    sessionStorage.setItem("paymentResolution_selectedStatuses", JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  const filterData = () => {
    let filtered = [...payoutCases];
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.case_number && String(item.case_number).toLowerCase().includes(t)) ||
          (item.payout_id && String(item.payout_id).toLowerCase().includes(t)) ||
          (item.shipment_id && String(item.shipment_id).toLowerCase().includes(t))
      );
    }
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((item) => selectedStatuses.includes(item?.status));
    }
    setFilteredData(filtered);
    setCurrentPage(0);
  };

  useEffect(() => { filterData(); }, [payoutCases, selectedStatuses, searchTerm]);

  const handleDownloadCase = async (row) => {
    try {
      setShowLoader(true);
      const response = await dispatch(downloadPayoutCase({ id: row.id })).unwrap();
      if (!response) throw new Error("Failed to download file.");
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${row?.shipment_id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      showToast(error.message || "Download failed", "error");
    } finally {
      setShowLoader(false);
    }
  };

  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => (checked ? [...prev, status] : prev.filter((s) => s !== status)));
  };

  const paginatedRows = filteredData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
  const activeFilterCount = countActiveFilters(selectedStatuses);

  const caseStats = useMemo(() => ({
    newCases: payoutCases.filter((d) => d.status === "NEW").length,
    investigating: payoutCases.filter((d) => d.status === "UNDER_INVESTIGATION").length,
    resolved: payoutCases.filter((d) => d.status === "RESOLVED").length,
  }), [payoutCases]);

  const columns = [
    { key: "case_number", label: "Case Number", width: 130 },
    { key: "payout_id", label: "Payout ID", width: 120 },
    { key: "shipment_id", label: "Shipment ID", width: 130 },
    { key: "walmart_shipping_id", label: "Walmart Shipment ID", width: 160 },
    { key: "walmart_inbound_id", label: "Walmart Inbound ID", width: 155 },
    {
      key: "quantity",
      label: "Quantity",
      width: 90,
      align: "center",
      render: (row) => (
        <span style={{ fontSize: 13 }}>
          {row.quantity_received} / {row.quantity_shipped}
        </span>
      ),
    },
    { key: "expectedDeliveryDate", label: "EDD", width: 160 },
    { key: "actualDeliveryDate", label: "ADD", width: 160 },
    { key: "createdDate", label: "Created At", width: 165 },
    {
      key: "status",
      label: "Status",
      width: 140,
      align: "center",
      render: (row) => <StatusBadge status={row?.status} />,
    },
    {
      key: "action",
      label: "Action",
      width: 88,
      align: "center",
      render: (row) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
          <Button
            variant="ghost"
            size="icon-sm"
            title="View & Edit Case"
            onClick={() => setCaseRow(row)}
          >
            <FilePenLine size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            title="Download case details"
            onClick={() => handleDownloadCase(row)}
          >
            <Download size={15} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader
          title="Payment Cases"
          subtitle="Keep track of all your orders and payments at a glance."
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "130px" : "160px"}, 1fr))`,
          gap: isMobile ? 10 : 14,
          marginBottom: 20,
        }}
      >
        <div style={statStyle("#e8b84b")}>
          <span style={statLabelStyle}>NEW</span>
          <span style={statValueStyle}>{caseStats.newCases}</span>
        </div>
        <div style={statStyle("#a78bfa")}>
          <span style={statLabelStyle}>INVESTIGATING</span>
          <span style={statValueStyle}>{caseStats.investigating}</span>
        </div>
        <div style={statStyle("#4caf50")}>
          <span style={statLabelStyle}>RESOLVED</span>
          <span style={statValueStyle}>{caseStats.resolved}</span>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? 10 : 12,
            flexWrap: "wrap",
            width: isMobile ? "100%" : "auto",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {!isMobile && <RefreshButton onRefresh={getAllPayoutCases} spinning={showLoader} />}
          <div style={{ flex: isMobile ? 1 : "none", minWidth: isMobile ? 0 : undefined }}>
            <SearchBar
              placeholder="Search by case, payout, shipment ID…"
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="default" style={{ width: isMobile ? "100%" : "auto" }}>
                <Filter size={14} /> Filter
                {activeFilterCount > 0 && (
                  <span
                    style={{
                      marginLeft: 4,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 16,
                      height: 16,
                      borderRadius: 8,
                      background: "#00ADB5",
                      color: "#000",
                      fontSize: 10,
                      fontWeight: 800,
                      padding: "0 4px",
                    }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{ minWidth: 220 }}>
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {CASE_STATUSES.map((s) => (
                <DropdownMenuCheckboxItem
                  key={s}
                  checked={selectedStatuses.includes(s)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) => handleStatusChange(s, checked)}
                >
                  {s.replace(/_/g, " ")}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button style={{ width: isMobile ? "100%" : "auto" }}>Create Case</Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={paginatedRows}
        totalRows={filteredData.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(n) => setCurrentPage(n)}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setCurrentPage(0); }}
        loading={showLoader}
        minWidth={1200}
      />
      <Dialog open={Boolean(caseRow)} onOpenChange={(open) => !open && setCaseRow(null)}>
        <DialogContent style={{ maxWidth: 980, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Payout Case</DialogTitle>
            <DialogDescription>{caseRow?.case_number ? `Case #${caseRow.case_number}` : "Review and update payout case details."}</DialogDescription>
          </DialogHeader>
          {caseRow && (
            <PayoutCasesPages
              dialogMode
              row={caseRow}
              onSuccess={() => {
                setCaseRow(null);
                getAllPayoutCases();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentCasesPages;
